import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getAnalysisEvidence, getAnalysisCollectors } from '../services/evidence';

const EvidencePage = () => {
  const { id } = useParams();
  const [evidenceList, setEvidenceList] = useState([]);
  const [collectors, setCollectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('website');

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      try {
        const [evidenceData, collectorsData] = await Promise.all([
          getAnalysisEvidence(id),
          getAnalysisCollectors(id)
        ]);
        setEvidenceList(evidenceData);
        setCollectors(collectorsData);
      } catch (error) {
        console.error("Failed to fetch evidence data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  if (loading) {
    return <div className="p-8 flex justify-center"><div className="animate-pulse">Loading evidence...</div></div>;
  }

  const tabs = [
    { id: 'website', label: 'Website' },
    { id: 'technology', label: 'Technology' },
    { id: 'social', label: 'Social Media' },
    { id: 'review', label: 'Reviews' },
    { id: 'jobs', label: 'Jobs' },
    { id: 'contact', label: 'Contacts' },
    { id: 'seo', label: 'SEO' },
  ];

  const tabToCategory = {
    website: 'website',
    technology: 'technology',
    social: 'social',
    review: 'customer_intelligence',
    jobs: 'hiring',
    contact: 'contact',
    seo: 'seo'
  };

  const currentEvidences = evidenceList.filter(e => e.category === tabToCategory[activeTab]);

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold mb-8 text-white">Collected Evidence</h1>

      <div className="flex flex-col md:flex-row gap-8">
        <div className="md:w-1/4">
          <nav className="flex flex-col space-y-2">
            {tabs.map(tab => {
              const collectorStatus = collectors.find(c => c.name === tab.id);
              const isCompleted = collectorStatus?.status === 'COMPLETED';

              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`text-left px-4 py-3 rounded-lg font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700'
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <span>{tab.label}</span>
                    {isCompleted ? (
                      <span className="w-2 h-2 rounded-full bg-green-500"></span>
                    ) : (
                      <span className="w-2 h-2 rounded-full bg-slate-500"></span>
                    )}
                  </div>
                </button>
              );
            })}
          </nav>
        </div>

        <div className="md:w-3/4">
          <div className="bg-slate-800 rounded-xl shadow-xl border border-slate-700 p-6">
            <h2 className="text-2xl font-semibold mb-4 capitalize text-white">{activeTab} Evidence</h2>

            {currentEvidences.length === 0 ? (
              <div className="text-slate-400 italic py-8 text-center border-2 border-dashed border-slate-600 rounded-lg">
                No evidence collected for {activeTab} yet.
              </div>
            ) : (
              <div className="space-y-6">
                {currentEvidences.map((evidence) => (
                  <div key={evidence.id} className="bg-slate-900 border border-slate-700 p-5 rounded-lg">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-lg font-bold text-white">{evidence.entity}</h3>
                        <div className="text-sm text-slate-400 mt-1">
                          Source: <a href={evidence.source_uri} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">{evidence.source_uri || evidence.collector}</a>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="inline-block px-2 py-1 bg-slate-800 text-slate-300 text-xs rounded border border-slate-700 mb-1">
                          Conf: {(evidence.confidence * 100).toFixed(0)}%
                        </span>
                        <div className="text-xs text-slate-500">
                          {new Date(evidence.collected_at.endsWith('Z') ? evidence.collected_at : evidence.collected_at + 'Z').toLocaleString()}
                        </div>
                      </div>
                    </div>

                    <div className="mb-4 text-slate-200">
                      <span className="font-semibold text-slate-400 block mb-1">Observation:</span>
                      <p>{evidence.fact}</p>
                    </div>

                    {evidence.metadata && Object.keys(evidence.metadata).length > 0 && (
                      <div>
                        <span className="font-semibold text-slate-400 block mb-2">Metadata:</span>
                        <pre className="bg-slate-950 text-slate-300 border border-slate-700 p-3 rounded overflow-x-auto text-xs shadow-inner">
                          {JSON.stringify(evidence.metadata, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EvidencePage;
